/* assets/custom-analytics.js
 * Single delegated analytics layer for the section/element/content convention.
 *  - Emits a universal `custom_click` engagement event (click / change / opt-in hover)
 *  - Fills the GA4 ecommerce gaps the Google & YouTube pixel does not send:
 *      select_item     (on card click, from data-ga4-items)
 *      view_item_list  (on card impression, batched per list)
 * Emits via gtag() directly (riadtile has no GTM container) with a dataLayer.push mirror
 * so the layer is GTM-ready later.
 * Spec: docs/plans/analytics-click-tracking.md
 * Depends on global.js (window.debounce / window.throttle), loaded before this.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var EL_ATTR = 'data-click-element';
  // Declares "we chose not to track this", subtree-wide. Purely a tooling marker for the
  // Atlas everywhere EXCEPT the select_item gate below, where it has to suppress: an
  // untracked control inside a card would otherwise resolve up to the card root and report
  // the shopper as having selected the product.
  var NOTRACK_ATTR = 'data-no-track';
  var SECTION_ATTR = 'data-click-section';
  var CONTENT_ATTR = 'data-click-content';

  // The tracked-element selector. `data-click-section` is deliberately NOT in it: section
  // lives on containers, so including it would make event.target.closest(SELECTOR) match a
  // section root on any stray click on its padding and report a phantom interaction.
  var SELECTOR = '[' + EL_ATTR + ']';

  // Dev surfaces where a defect should be loud. `Shopify.designMode` is NOT usable for this:
  // it is only set inside the admin theme-editor iframe and is undefined under
  // `shopify theme dev`, which is the environment the developer actually works in.
  var IS_DEV =
    /^(127\.0\.0\.1|localhost)$/.test(location.hostname) ||
    /\.shopifypreview\.com$/.test(location.hostname) ||
    location.search.indexOf('preview_theme_id=') > -1;

  // ---- dimension read: three attributes, no string splitting ----
  // An element is properly tracked only if it has BOTH an element name and a resolvable
  // section ancestor. Having one without the other is a defect, not a variant:
  //
  //   element + section  -> tracked
  //   data-no-track      -> declared, deliberately not measured
  //   neither            -> a gap
  //   element, no section-> ALSO a gap. It looks instrumented but has nowhere to attribute
  //                         to, which is worse than an obvious hole because it counts toward
  //                         coverage.
  //
  // A gap SENDS NOTHING. An event missing either half is not a weaker event, it is an
  // unattributable one: it inflates custom_click totals, and it lands in the
  // merchandiser's reports as a row she cannot act on or explain. Neither a sentinel value
  // nor `(not set)` is better than absence — both put a defect in front of the person least
  // able to fix it. Gaps surface to the developer instead, via the console error below and
  // the load sweep at the bottom of this file, where they can still be fixed.
  function readDims(el) {
    var scope = el.closest('[' + SECTION_ATTR + ']');
    if (!scope && IS_DEV && !warnedNoSection.has(el)) {
      warnedNoSection.add(el);
      console.error(
        '[analytics] GAP: tracked element has no data-click-section ancestor, so NOTHING ' +
          'is sent for it. Add a section root, or data-no-track if it should not be ' +
          'measured.',
        el
      );
    }
    return {
      section: scope ? scope.getAttribute(SECTION_ATTR) : null, // null => gap, do not emit
      element: el.getAttribute(EL_ATTR),
      content: el.getAttribute(CONTENT_ATTR) || null
    };
  }
  var warnedNoSection = new WeakSet();

  function readJSON(el, attr) {
    var raw = el.getAttribute(attr);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('[analytics] bad JSON in ' + attr, el, raw);
      }
      return null;
    }
  }

  function gtagEvent(name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
  }

  // ---- engagement: custom_click ----
  function emit(el, interaction, extra) {
    var d = readDims(el);
    // Both halves or nothing. See readDims for why a gap is silent rather than partial.
    if (!d.element || !d.section) return;
    // These key names must match the GA4 custom dimensions EXACTLY — they are registered
    // as `section` / `element` / `click_content`. Renaming either side alone silently drops
    // the dimension: an unregistered parameter is discarded on receipt and, with no
    // BigQuery export, is unrecoverable.
    //
    // `click_content` is prefixed where the other two are bare because a bare `content`
    // is not a free name: GA4 reads it as the campaign's ad content (utm_content's slot)
    // and relabels the whole session `(not set) / (not set)`, i.e. Unassigned. Shipped
    // bare from 2026-08-10, it moved half of all GA4 revenue into Unassigned. Never send
    // a bare `content`, `source`, `medium`, `campaign` or `term`.
    var payload = Object.assign(
      {
        interaction: interaction,
        section: d.section,
        element: d.element,
        click_content: d.content
      },
      extra || {}
    );
    gtagEvent('custom_click', payload);
    window.dataLayer.push(Object.assign({ event: 'custom_click' }, payload));
  }

  // A stable per-element key for dedupe and fire-once bookkeeping. section/element is NOT
  // unique — every link in one nav panel shares it — so a string key would let the 300ms
  // cooldown swallow a click on a genuinely different element. Keyed on the NODE instead,
  // which is unique by construction.
  var keySeq = 0;
  var keyMap = new WeakMap();
  function nodeKey(el) {
    var k = keyMap.get(el);
    if (!k) {
      k = ++keySeq;
      keyMap.set(el, k);
    }
    return k;
  }

  // ---- ecommerce: select_item ----
  function fireSelectItem(el) {
    var items = readJSON(el, 'data-ga4-items');
    if (!items || !items.length) return;
    gtagEvent('select_item', {
      items: items,
      item_list_id: items[0].item_list_id,
      item_list_name: items[0].item_list_name
    });
  }

  // ---- dedupe: per element+interaction cooldown ----
  var last = new Map();
  var COOLDOWN_MS = 300;
  function shouldFire(key) {
    var now = Date.now();
    var prev = last.get(key);
    if (prev && now - prev < COOLDOWN_MS) return false;
    last.set(key, now);
    if (last.size > 600) last.clear();
    return true;
  }

  // ---- CLICK ----
  document.addEventListener(
    'click',
    function (event) {
      var el = event.target.closest(SELECTOR);
      if (el && shouldFire('click|' + nodeKey(el))) {
        emit(el, 'click');
      }
      // select_item resolves to the nearest item-carrying ancestor (the card root),
      // so it still fires when the click lands on an inner zone (image vs title) — but
      // NOT when the click is a non-navigational control inside the card (e.g. quick-add),
      // which is its own interaction rather than an item selection.
      var ecomEl = event.target.closest('[data-ga4-items]');
      if (ecomEl && ecomEl.getAttribute('data-ga4-event') === 'select_item') {
        // Which clicks inside a card count as selecting the item. Fail-OPEN: a click in a
        // card is a selection unless the leaf says otherwise with data-click-nonnav. This
        // used to infer the answer from an element-kind allow-list, which meant the gate
        // silently changed behaviour whenever someone coined a new element name — now that
        // element values are free-form, inference is not available and an explicit opt-out
        // is the only honest mechanism. The opt-out sites are quick-adds and the category
        // label; see snippets/card-product.liquid.
        var leaf = event.target.closest(SELECTOR);
        var navZone =
          !event.target.closest('[' + NOTRACK_ATTR + ']') &&
          (!leaf || !leaf.hasAttribute('data-click-nonnav'));
        if (navZone && shouldFire('select|' + (ecomEl.getAttribute('data-ga4-items') || ''))) {
          fireSelectItem(ecomEl);
        }
      }
    },
    { passive: true }
  );

  // ---- CHANGE (selects / inputs / checkboxes) ----
  // A control's value is only read when the control is structurally incapable of holding
  // free text: a <select>, a checkbox/radio, or a numeric input. Everything else — text,
  // search, email, tel, textarea — emits the interaction with NO value. This is an
  // allow-list by design: a deny-list of known-bad fields decays the moment someone adds
  // a new one, and the failure is silent and unrecoverable (no BigQuery to audit against).
  // Note `inputmode` is load-bearing: the price facet inputs are type="text"
  // inputmode="decimal" (snippets/price-facet.liquid:11-12), so a type="number"-only
  // rule would silently drop price-filter data.
  function readableValue(el) {
    if (el.tagName === 'SELECT') return el.value;
    if (el.tagName !== 'INPUT') return null;
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
    if (el.type === 'number') return el.value;
    var mode = (el.getAttribute('inputmode') || '').toLowerCase();
    if (mode === 'numeric' || mode === 'decimal') return el.value;
    return null;
  }

  document.addEventListener(
    'change',
    function (event) {
      var el = event.target.closest(SELECTOR);
      if (!el) return;
      // `change` bubbles, so without this an UNTRACKED control inside a tracked ancestor
      // has its change attributed to that ancestor. A change is only meaningful when the
      // tracked element IS the control. Deliberately not applied to `click`, where
      // resolving through descendants is the whole point — a click on the icon inside a
      // button must attribute to the button.
      if (event.target !== el) return;
      if (!shouldFire('change|' + nodeKey(el))) return;
      var value = readableValue(el);
      emit(el, 'change', value === null ? undefined : { value: value });
    },
    { passive: true }
  );

  // ---- OPT-IN hover (data-hover-track), debounced, once per element per pageview ----
  if (typeof window.debounce === 'function') {
    var hoverFired = new WeakSet();
    var onHover = window.debounce(function (el) {
      if (hoverFired.has(el)) return;
      hoverFired.add(el);
      emit(el, 'hover');
    }, 250);
    document.addEventListener(
      'mouseover',
      function (event) {
        var el = event.target.closest(SELECTOR);
        if (!el || !el.hasAttribute('data-hover-track')) return;
        if (el.contains(event.relatedTarget)) return; // crossed between children of same leaf
        onHover(el);
      },
      { passive: true }
    );
  }

  // ---- debounced typing: REMOVED ----
  // There was a `data-input-track` listener here that fired on every typing pause with the
  // raw field contents, and additionally copied them into `search_term`. On the two search
  // inputs that meant shopper-typed text — people type addresses and email addresses into
  // site search — and it also fed GA4's native Search term dimension keystroke fragments
  // ("ze", "zell") alongside real queries. Aggregate "what do people search for" is already
  // covered natively by Enhanced Measurement's view_search_results, so nothing is lost.
  // Do not reintroduce this without a control-type allow-list (see readableValue above).

  // ---- view_item_list impressions ----
  // ONE consolidated event per list, fired the first time any card of that list scrolls
  // into view, carrying every item of that list currently in the DOM (Google's canonical
  // view_item_list pattern). Keyed by item_list_id so scrolling never re-fires it.
  if ('IntersectionObserver' in window) {
    var sentLists = {}; // item_list_id -> true once its view_item_list has fired
    var handled = new WeakSet(); // cards already processed (avoid re-observe churn)

    function itemsForList(listId) {
      var collected = [];
      document.querySelectorAll('[data-ga4-items]').forEach(function (node) {
        var its = readJSON(node, 'data-ga4-items');
        if (!its || !its.length) return;
        if ((its[0].item_list_id || 'list') !== listId) return;
        for (var i = 0; i < its.length; i++) collected.push(its[i]);
      });
      return collected;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          io.unobserve(el);
          handled.add(el);
          var items = readJSON(el, 'data-ga4-items');
          if (!items || !items.length) return;
          var listId = items[0].item_list_id || 'list';
          if (sentLists[listId]) return;
          sentLists[listId] = true;
          var listItems = itemsForList(listId).slice(0, 200); // GA4 ~200-item ceiling
          if (!listItems.length) return;
          gtagEvent('view_item_list', {
            items: listItems,
            item_list_id: listId,
            item_list_name: listItems[0].item_list_name
          });
        });
      },
      { threshold: 0.1 }
    );

    var observeAll = function () {
      document.querySelectorAll('[data-ga4-items]').forEach(function (el) {
        if (!handled.has(el)) io.observe(el);
      });
    };

    var startObserving = function () {
      observeAll();
      // Re-scan for nodes added by facet AJAX swaps / lazy-loaded recommendations.
      if ('MutationObserver' in window) {
        new MutationObserver(function (muts) {
          for (var i = 0; i < muts.length; i++) {
            if (muts[i].addedNodes && muts[i].addedNodes.length) {
              observeAll();
              return;
            }
          }
        }).observe(document.body, { childList: true, subtree: true });
      }
    };

    if (document.readyState !== 'loading') startObserving();
    else document.addEventListener('DOMContentLoaded', startObserving);
  }

  // ---- design-mode helper: outline tracked elements in the theme editor ----
  if (window.Shopify && window.Shopify.designMode) {
    var mark = function () {
      document.querySelectorAll(SELECTOR).forEach(function (el) {
        el.style.outline = '1px dashed rgba(255,0,128,.4)';
      });
    };
    if (document.readyState !== 'loading') mark();
    else document.addEventListener('DOMContentLoaded', mark);
  }

  // ---- dev sweep: report unattributed elements at load, not on click ----
  // The click-time warning in readDims only fires if somebody happens to click the broken
  // element. This lists every one of them the moment the page renders, which is what makes
  // "did I forget a section root" answerable during `shopify theme dev` — which is the only
  // place it CAN be answered now, because a gap sends nothing and so leaves no trace in GA4.
  if (IS_DEV) {
    var sweep = function () {
      var orphans = [];
      document.querySelectorAll(SELECTOR).forEach(function (el) {
        if (!el.closest('[' + SECTION_ATTR + ']')) orphans.push(el);
      });
      if (!orphans.length) return;
      console.error(
        '[analytics] ' +
          orphans.length +
          ' tracked element(s) have no data-click-section ancestor, so nothing is sent for ' +
          'them. Each needs a section root, or data-no-track if it should not be measured.',
        orphans
      );
    };
    if (document.readyState !== 'loading') sweep();
    else document.addEventListener('DOMContentLoaded', sweep);
  }
})();
